import type { JSX } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'
import { useSkinUrl } from '@/ui/hooks/useSkinUrl'

/**
 * Голова бота — объёмная, из его настоящего скина.
 *
 * Почему CSS 3D, а не three.js. На дашборде и в списке аватарок десятки; каждый
 * WebGL-контекст стоит памяти и драйверных ресурсов, а браузер их вообще
 * ограничивает (~16). Куб из шести `div` с `transform` рисует композитор, стоит
 * почти ничего и анимируется на GPU. Для головы этого достаточно: геометрия —
 * буквально куб.
 *
 * Текстура берётся из нашего прокси (`/api/skins/:uuid.png`), а не напрямую из
 * Mojang: CSP не пускает внешние хосты, плюс кэш и приватность.
 *
 * Раскладка скина Minecraft 64×64, зона головы (в пикселях текстуры):
 *   right 0,8   front 8,8   left 16,8   back 24,8   top 8,0   bottom 16,0
 * Каждая грань 8×8, поэтому масштабируем фон в 8 раз от размера грани и
 * позиционируем отрицательными смещениями. `image-rendering: pixelated` —
 * иначе 8×8 превращается в мыло.
 */

export interface SkinHeadProps {
  /** UUID Minecraft-аккаунта; `null` для offline — отдастся силуэт. */
  uuid: string | null
  /** Ник: и для alt, и как ключ выбора дефолтного силуэта. */
  name: string
  /** Сторона грани в пикселях. 40 — для карточки, 96 — для страницы бота. */
  size?: number
  /** Медленное вращение: «живой» аватар. Выключается при reduced-motion. */
  spin?: boolean
  className?: string
}

const FACES = [
  { key: 'front', x: 8, y: 8, transform: 'translateZ(var(--half))' },
  { key: 'back', x: 24, y: 8, transform: 'rotateY(180deg) translateZ(var(--half))' },
  { key: 'right', x: 0, y: 8, transform: 'rotateY(-90deg) translateZ(var(--half))' },
  { key: 'left', x: 16, y: 8, transform: 'rotateY(90deg) translateZ(var(--half))' },
  { key: 'top', x: 8, y: 0, transform: 'rotateX(90deg) translateZ(var(--half))' },
  { key: 'bottom', x: 16, y: 0, transform: 'rotateX(-90deg) translateZ(var(--half))' }
] as const

export function SkinHead({
  uuid,
  name,
  size = 40,
  spin = true,
  className
}: SkinHeadProps): JSX.Element {
  const id = useId()
  // Скин качается с Bearer и приходит blob-URL: роут не публичный, а браузер к
  // фоновой картинке заголовки не добавляет (см. useSkinUrl).
  const url = useSkinUrl(uuid, name)
  const half = size / 2

  return (
    <div
      className={cn('relative shrink-0', className)}
      style={{ width: size, height: size, perspective: size * 4 }}
      role="img"
      aria-label={`Аватар ${name}`}
    >
      <div
        className={spin ? 'skin-spin' : undefined}
        style={{
          // @ts-expect-error — кастомное свойство для грани
          '--half': `${half}px`,
          width: size,
          height: size,
          position: 'relative',
          transformStyle: 'preserve-3d',
          // Небольшой наклон: с чистого фронта куб не читается как объём.
          transform: spin ? undefined : 'rotateX(-12deg) rotateY(-24deg)'
        }}
      >
        {FACES.map(face => (
          <span
            key={`${id}-${face.key}`}
            aria-hidden
            style={{
              position: 'absolute',
              inset: 0,
              transform: face.transform,
              // Пока текстура не пришла — грань просто пустая: показывать
              // «битую картинку» хуже, чем ничего.
              backgroundImage: url === null ? undefined : `url(${url})`,
              backgroundColor: url === null ? 'var(--color-panel-3)' : undefined,
              backgroundSize: `${size * 8}px ${size * 8}px`,
              backgroundPosition: `-${face.x * (size / 8)}px -${face.y * (size / 8)}px`,
              imageRendering: 'pixelated',
              backfaceVisibility: 'hidden'
            }}
          />
        ))}
      </div>
    </div>
  )
}
